import * as Api from '@ViewModels';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LevStandardQueryMeta } from '../../web/ReactQueryProvider';
import { useUserSession } from '../../models/hooks/appStateHooks';

const SHARED_MAILBOX_PATH = 'user/preferences/sharedMailbox';

// Scoped by impersonation context so an admin impersonating a customer reads/writes the customer's settings.
export const sharedMailboxQueryKey = (impersonationContext?: Api.IImpersonationContext) =>
	['sharedMailboxSettings', impersonationContext] as const;

export function useSharedMailboxQuery({
	impersonationContext,
	enabled = true,
	refetchOnWindowFocus = false,
	meta,
}: {
	impersonationContext?: Api.IImpersonationContext;
	enabled?: boolean;
	refetchOnWindowFocus?: boolean;
	meta?: LevStandardQueryMeta;
} = {}) {
	const userSession = useUserSession();
	return useQuery({
		enabled,
		refetchOnWindowFocus,
		queryFn: () => {
			// A 404 means "no shared mailbox configured / not available" — resolve to null instead of erroring (and toasting).
			return userSession.webServiceHelper.callAsync<Api.ISharedMailboxSettings>({
				path: Api.ImpersonationBroker.composeApiUrl({ impersonationContext, urlPath: SHARED_MAILBOX_PATH }),
				method: 'GET',
				throwOn404: false,
			});
		},
		queryKey: sharedMailboxQueryKey(impersonationContext),
		meta,
	});
}

// On a 400 the backend returns a user-displayable `systemMessage`; surface it via `onError`.
export function useUpdateSharedMailboxMutation({
	impersonationContext,
	onError,
	onSuccess,
}: {
	impersonationContext?: Api.IImpersonationContext;
	onError?: (error: Api.IOperationResultNoValue) => void;
	onSuccess?: (data: Api.ISharedMailboxSettings) => void;
}) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ emailAddress, name }: { emailAddress: string; name?: string }) => {
			return userSession.webServiceHelper.callAsync<Api.ISharedMailboxSettings>(
				Api.ImpersonationBroker.composeApiUrl({ impersonationContext, urlPath: SHARED_MAILBOX_PATH }),
				'PUT',
				{ emailAddress, name }
			);
		},
		onError,
		onSuccess: data => {
			queryClient.setQueryData(sharedMailboxQueryKey(impersonationContext), data);
			onSuccess?.(data);
		},
	});
}

export function useDeleteSharedMailboxMutation({
	impersonationContext,
	onError,
	onSuccess,
}: {
	impersonationContext?: Api.IImpersonationContext;
	onError?: (error: Api.IOperationResultNoValue) => void;
	onSuccess?: (data: Api.ISharedMailboxSettings) => void;
}) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: () => {
			return userSession.webServiceHelper.callAsync<Api.ISharedMailboxSettings>(
				Api.ImpersonationBroker.composeApiUrl({ impersonationContext, urlPath: SHARED_MAILBOX_PATH }),
				'DELETE'
			);
		},
		onError,
		onSuccess: data => {
			queryClient.setQueryData(sharedMailboxQueryKey(impersonationContext), data);
			onSuccess?.(data);
		},
	});
}

export function useTestSharedMailboxMutation({
	impersonationContext,
	onError,
	onSuccess,
}: {
	impersonationContext?: Api.IImpersonationContext;
	onError?: (error: Api.IOperationResultNoValue) => void;
	onSuccess?: () => void;
}) {
	const userSession = useUserSession();
	return useMutation({
		mutationFn: () => {
			// Verification reuses EmailController's existing test-send (POST email/sendTest). It has no body —
			// the backend sends from the user's configured send-from (the shared mailbox once saved).
			return userSession.webServiceHelper.callAsync<void>(
				Api.ImpersonationBroker.composeApiUrl({ impersonationContext, urlPath: 'email/sendTest' }),
				'POST'
			);
		},
		onError,
		onSuccess,
	});
}
